<template>
  <div>
    <head>
      <title>Procedure History | County Hospital</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar/>
      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Header -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Procedure History</h4>
                  <div class="page-title-right">
                    <button class="btn btn-sm btn-outline-primary me-2" @click="$router.push('scheduled-procedures')">
                      <i class="bx bx-list-ul me-1"></i> Scheduled
                    </button>
                    <button class="btn btn-sm btn-success" @click="exportCsv">
                      <i class="bx bx-download me-1"></i> Export CSV
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters -->
            <div class="row g-2 mb-3 align-items-end">
              <div class="col-md-2">
                <label class="form-label small mb-1">Outcome</label>
                <select class="form-select form-select-sm" v-model="filters.outcome" @change="load">
                  <option value="">All Outcomes</option>
                  <option value="SUCCESSFUL">Successful</option>
                  <option value="COMPLICATIONS">With Complications</option>
                  <option value="UNSUCCESSFUL">Unsuccessful</option>
                  <option value="ABANDONED">Abandoned</option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label small mb-1">Priority</label>
                <select class="form-select form-select-sm" v-model="filters.priority" @change="load">
                  <option value="">All</option>
                  <option value="ROUTINE">Routine</option>
                  <option value="URGENT">Urgent</option>
                  <option value="EMERGENCY">Emergency</option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label small mb-1">From</label>
                <input type="date" class="form-control form-control-sm" v-model="filters.dateFrom">
              </div>
              <div class="col-md-2">
                <label class="form-label small mb-1">To</label>
                <input type="date" class="form-control form-control-sm" v-model="filters.dateTo">
              </div>
              <div class="col-md-3">
                <label class="form-label small mb-1">Search</label>
                <input type="text" class="form-control form-control-sm"
                       v-model.trim="search"
                       placeholder="patient, procedure, doctor, ref...">
              </div>
              <div class="col-md-1">
                <button class="btn btn-primary btn-sm w-100" @click="load">
                  <i class="bx bx-filter"></i>
                </button>
              </div>
            </div>

            <!-- KPI strip -->
            <div class="row g-2 mb-3">
              <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-muted">Total</small><h5 class="mb-0">{{ filtered.length }}</h5></div></div>
              <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-success">Successful</small><h5 class="mb-0 text-success">{{ counts.successful }}</h5></div></div>
              <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-warning">Complications</small><h5 class="mb-0 text-warning">{{ counts.complications }}</h5></div></div>
              <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-danger">Unsuccessful</small><h5 class="mb-0 text-danger">{{ counts.unsuccessful }}</h5></div></div>
            </div>

            <!-- States -->
            <div v-if="loading" class="text-center text-muted py-4">
              <span class="spinner-border spinner-border-sm me-1"></span>Loading…
            </div>
            <div v-else-if="!filtered.length" class="text-center text-muted py-4">
              No completed procedures match these filters.
            </div>

            <!-- Card list (mobile) / table (tablet+) -->
            <div v-else>
              <div class="d-md-none">
                <div v-for="p in filtered" :key="'card-'+p.procedureID"
                     class="card border mb-2 shadow-sm"
                     @click="openDetail(p)" style="cursor:pointer;">
                  <div class="card-body p-3">
                    <div class="d-flex justify-content-between align-items-start mb-1">
                      <strong>{{ p.procedureName }}</strong>
                      <span class="badge" :class="outcomeBadge(p.outcome)">{{ p.outcome || '—' }}</span>
                    </div>
                    <div>{{ p.patientName }} <small class="text-muted">#{{ p.patientNo }}</small></div>
                    <div class="text-muted small">
                      Performed: {{ formatDate(p.datePerformed) }} · Dr. {{ p.performedBy || p.performingDoctorName || '—' }}
                    </div>
                    <small v-if="p.complications" class="d-block text-warning mt-1">
                      <i class="bx bx-error-circle"></i> Complications recorded
                    </small>
                  </div>
                </div>
              </div>

              <div class="table-responsive d-none d-md-block">
                <table class="table table-sm table-hover align-middle">
                  <thead class="table-light">
                  <tr>
                    <th>Reference</th>
                    <th>Patient</th>
                    <th>Procedure</th>
                    <th>Performed</th>
                    <th>Doctor</th>
                    <th>Outcome</th>
                    <th>Complications</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="p in filtered" :key="'row-'+p.procedureID"
                      @click="openDetail(p)" style="cursor:pointer;">
                    <td><code>{{ p.procedureNo }}</code></td>
                    <td><strong>{{ p.patientName }}</strong><br><small class="text-muted">#{{ p.patientNo }}</small></td>
                    <td>{{ p.procedureName }}</td>
                    <td>{{ formatDate(p.datePerformed) }}</td>
                    <td>{{ p.performedBy || p.performingDoctorName || '—' }}</td>
                    <td><span class="badge" :class="outcomeBadge(p.outcome)">{{ p.outcome || '—' }}</span></td>
                    <td>
                      <span v-if="p.complications" class="text-warning">
                        <i class="bx bx-error-circle"></i> Yes
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Detail modal -->
            <div class="modal fade" id="historyDetail" tabindex="-1">
              <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content" v-if="selected">
                  <div class="modal-header">
                    <div>
                      <h5 class="modal-title">{{ selected.procedureName }}</h5>
                      <small class="text-muted">{{ selected.procedureNo }}</small>
                    </div>
                    <button class="btn-close" data-bs-dismiss="modal"></button>
                  </div>
                  <div class="modal-body">
                    <div class="row g-3 mb-3">
                      <div class="col-md-6"><div class="text-muted small">Patient</div><strong>{{ selected.patientName }}</strong> <small>#{{ selected.patientNo }}</small></div>
                      <div class="col-md-6"><div class="text-muted small">Visit</div>{{ selected.visitNo || selected.visitID || '—' }}</div>
                      <div class="col-md-6"><div class="text-muted small">Scheduled</div>{{ formatDate(selected.scheduledDate) }} {{ formatTime(selected.scheduledTime) }}</div>
                      <div class="col-md-6"><div class="text-muted small">Performed</div>{{ formatDate(selected.datePerformed) }}</div>
                      <div class="col-md-6"><div class="text-muted small">Doctor</div>{{ selected.performedBy || selected.performingDoctorName || '—' }}</div>
                      <div class="col-md-6"><div class="text-muted small">Outcome</div><span class="badge" :class="outcomeBadge(selected.outcome)">{{ selected.outcome || '—' }}</span></div>
                    </div>
                    <h6>Pre-op Diagnosis</h6>
                    <p class="text-muted">{{ selected.preOpDiagnosis || '—' }}</p>
                    <h6>Post-op Diagnosis</h6>
                    <p class="text-muted">{{ selected.postOpDiagnosis || '—' }}</p>
                    <h6 v-if="selected.procedureNotes">Procedure Notes</h6>
                    <p class="text-muted" v-if="selected.procedureNotes">{{ selected.procedureNotes }}</p>
                    <h6 v-if="selected.complications">Complications</h6>
                    <p class="text-warning" v-if="selected.complications">
                      <i class="bx bx-error-circle me-1"></i>{{ selected.complications }}
                    </p>
                  </div>
                  <div class="modal-footer">
                    <button class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { execute, hospital, getRight } from "@/api";

export default {
  name: "ProcedureHistory",
  components: { NavigationBar },
  data() {
    return {
      loading: false,
      procedures: [],
      filters: { outcome: '', priority: '', dateFrom: '', dateTo: '' },
      search: '',
      selected: null
    };
  },
  computed: {
    filtered() {
      const q = (this.search || '').toLowerCase().trim();
      let out = this.procedures;
      if (this.filters.outcome) {
        out = out.filter(p => (p.outcome || '').toUpperCase() === this.filters.outcome);
      }
      if (this.filters.priority) {
        out = out.filter(p => (p.priority || '') === this.filters.priority);
      }
      if (q) {
        out = out.filter(p => {
          const blob = [
            p.procedureNo, p.patientNo, p.patientName, p.procedureName,
            p.performedBy, p.performingDoctorName, p.preOpDiagnosis, p.postOpDiagnosis
          ].filter(Boolean).join(' ').toLowerCase();
          return blob.includes(q);
        });
      }
      return out;
    },
    counts() {
      let s = 0, c = 0, u = 0;
      for (const p of this.filtered) {
        const o = (p.outcome || '').toUpperCase();
        if (o === 'SUCCESSFUL')          s++;
        if (p.complications)             c++;
        if (o === 'UNSUCCESSFUL' || o === 'ABANDONED') u++;
      }
      return { successful: s, complications: c, unsuccessful: u };
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    getRight(t) { return getRight(t); },
    load() {
      this.loading = true;
      const data = new FormData();
      data.append('function', 'getScheduledProcedures');
      data.append('status', 'COMPLETED');
      const facilityID = sessionStorage.getItem('facilityID') || '';
      if (facilityID) data.append('facilityID', facilityID);
      if (this.filters.dateFrom) data.append('dateFrom', this.filters.dateFrom);
      if (this.filters.dateTo)   data.append('dateTo',   this.filters.dateTo);
      execute(data, hospital).then(res => {
        this.loading = false;
        this.procedures = (res.data && res.data.success && res.data.data.procedures) || [];
      }).catch(() => {
        this.loading = false;
        this.procedures = [];
      });
    },
    openDetail(p) {
      this.selected = p;
      this.$nextTick(() => {
        const el = document.getElementById('historyDetail');
        if (el && window.bootstrap) new window.bootstrap.Modal(el).show();
      });
    },
    exportCsv() {
      if (!this.filtered.length) return;
      const headers = ['Reference','Patient No','Patient','Procedure','Performed','Doctor','Outcome','Complications','Pre-op','Post-op'];
      const rows = this.filtered.map(p => [
        p.procedureNo, p.patientNo, p.patientName, p.procedureName,
        this.formatDate(p.datePerformed),
        p.performedBy || p.performingDoctorName || '',
        p.outcome || '',
        p.complications ? 'YES' : 'NO',
        (p.preOpDiagnosis || '').replace(/[\r\n,]/g, ' '),
        (p.postOpDiagnosis || '').replace(/[\r\n,]/g, ' ')
      ]);
      const csv = [headers, ...rows]
          .map(r => r.map(c => '"' + String(c == null ? '' : c).replace(/"/g, '""') + '"').join(','))
          .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'procedure-history-' + new Date().toISOString().slice(0,10) + '.csv';
      link.click();
      URL.revokeObjectURL(link.href);
    },
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    formatTime(t) { return t ? t.toString().slice(0, 5) : ''; },
    outcomeBadge(o) {
      const u = (o || '').toUpperCase();
      if (u === 'SUCCESSFUL')    return 'bg-success';
      if (u === 'COMPLICATIONS') return 'bg-warning text-dark';
      if (u === 'UNSUCCESSFUL')  return 'bg-danger';
      if (u === 'ABANDONED')     return 'bg-secondary';
      return 'bg-light text-dark';
    }
  }
};
</script>
